import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { NotFound } from '@/shared/components/NotFound';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { AdminLayout } from '@/layouts/AdminLayout';
import { DemoLayout } from '@/layouts/DemoLayout';
import { MyAccountPage } from '@/pages/account/MyAccountPage';
import { SettingsAccountsPage } from '@/pages/settings/SettingsAccountsPage';
import { SettingsAccountDetailPage } from '@/pages/settings/SettingsAccountDetailPage';
import { SettingsAuditLogPage } from '@/pages/settings/SettingsAuditLogPage';
import { SettingsMenusPage } from '@/pages/settings/SettingsMenusPage';
import { SettingsPartnersPage } from '@/pages/settings/SettingsPartnersPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { DrugsPage } from '@/pages/drugs/DrugsPage';
import { DrugDetailPage } from '@/pages/drugs/DrugDetailPage';
import { InstitutionsPage } from '@/pages/institutions/InstitutionsPage';
import { InstitutionDetailPage } from '@/pages/institutions/InstitutionDetailPage';
import { ForceChangePasswordPage } from '@/pages/login/ForceChangePasswordPage';
import { LoginPage } from '@/pages/login/LoginPage';
import { PharmaCompaniesPage } from '@/pages/pharma-companies/PharmaCompaniesPage';
import { PharmaCompanyCreatePage } from '@/pages/pharma-companies/PharmaCompanyCreatePage';
import { PharmaCompanyDetailPage } from '@/pages/pharma-companies/PharmaCompanyDetailPage';
import { PharmaciesPage } from '@/pages/pharmacies/PharmaciesPage';
import { PharmacyDetailPage } from '@/pages/pharmacies/PharmacyDetailPage';
import { RegisterPage } from '@/pages/register/RegisterPage';
import { PrescriptionReviewPage } from '@/pages/prescriptions/PrescriptionReviewPage';
import { PrescriptionReviewDetailPage } from '@/pages/prescriptions/PrescriptionReviewDetailPage';
import { PreviousPrescriptionDrugsWindowPage } from '@/pages/prescriptions/PreviousPrescriptionDrugsWindowPage';
import { PrescriptionHistoryPage } from '@/pages/prescriptions/PrescriptionHistoryPage';
import { PrescriptionHistoryDetailPage } from '@/pages/prescriptions/PrescriptionHistoryDetailPage';
import { PrescriptionRegistrationDeadlinePage } from '@/pages/prescriptions/PrescriptionRegistrationDeadlinePage';
import { SettlementFeesPage } from '@/pages/settlement-fees/SettlementFeesPage';
import { SettlementFeeDetailPage } from '@/pages/settlement-fees/SettlementFeeDetailPage';
import { SettlementUploadPage } from '@/pages/settlements/SettlementUploadPage';
import { SettlementUploadDetailPage } from '@/pages/settlements/SettlementUploadDetailPage';
import { PrescriptionSettlementsPage } from '@/pages/settlements/PrescriptionSettlementsPage';
import { PrescriptionSettlementDetailPage } from '@/pages/settlements/PrescriptionSettlementDetailPage';
import { ClientsPage } from '@/pages/clients/ClientsPage';
import { ClientDetailPage } from '@/pages/clients/ClientDetailPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { UserDetailPage } from '@/pages/users/UserDetailPage';
import { PartnerCompanyPage } from '@/pages/partner-company/PartnerCompanyPage';
import { PartnerCompanyCreatePage } from '@/pages/partner-company/PartnerCompanyCreatePage';
import { PartnerCompanyDetailPage } from '@/pages/partner-company/PartnerCompanyDetailPage';
import { ContractAppendixPage } from '@/pages/contracts/ContractAppendixPage';
import { BaseFeeRatesPage } from '@/pages/fee-rates/BaseFeeRatesPage';
import { BaseFeeRateSettingPage } from '@/pages/fee-rates/BaseFeeRateSettingPage';
import { DetailFeeRatesPage } from '@/pages/fee-rates/DetailFeeRatesPage';
import { DetailFeeRateSettingPage } from '@/pages/fee-rates/DetailFeeRateSettingPage';
import { ReverseTaxPage } from '@/pages/settlements/ReverseTaxPage';
import { DrugDataCreatePage } from '@/pages/drugs/DrugDataCreatePage';
import { FaqPage } from '@/pages/board/FaqPage';
import { AnnouncementsPage } from '@/pages/board/AnnouncementsPage';
import { TermsFormPage } from '@/pages/terms/TermsFormPage';
import { TermsPage } from '@/pages/terms/TermsPage';
import { AnnouncementFormPage } from '@/pages/board/AnnouncementFormPage';
import { MaxMatchOperatingHoursPage } from '@/pages/max-match/MaxMatchOperatingHoursPage';
import { MaxMatchInfoPage } from '@/pages/max-match/MaxMatchInfoPage';
import { MaxMatchInfoDetailPage } from '@/pages/max-match/MaxMatchInfoDetailPage';
import { MaxMatchMiniPage } from '@/pages/max-match/MaxMatchMiniPage';
import { MaxMatchMiniDetailPage } from '@/pages/max-match/MaxMatchMiniDetailPage';
import { MaxMatchFullPage } from '@/pages/max-match/MaxMatchFullPage';
import { MaxMatchFullDetailPage } from '@/pages/max-match/MaxMatchFullDetailPage';
import { MaxMatchOpsPage } from '@/pages/max-match/MaxMatchOpsPage';
import { MaxMatchOpsDetailPage } from '@/pages/max-match/MaxMatchOpsDetailPage';
import { SettlementInfoGeneratorPage } from '@/pages/settlements/SettlementInfoGeneratorPage';
import { CommissionRateAggregatorPage } from '@/pages/settlement-fees/CommissionRateAggregatorPage';
import { DemoHomePage } from '@/pages/demo/DemoHomePage';
import { DemoPostProcessPage } from '@/pages/demo/DemoPostProcessPage';
import { DemoBatchJobsPage } from '@/pages/demo/DemoBatchJobsPage';
import { DemoBatchJobDetailPage } from '@/pages/demo/DemoBatchJobDetailPage';
import { passThroughSearch } from '@/shared/lib/listSearchParams';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';

const removeAdminPrefix = (path: string) => {
  return path.replace(ROUTE_PATHS.ADMIN, '');
};

const removeDemoPrefix = (path: string) => {
  const stripped = path.replace(ROUTE_PATHS.DEMO, '');
  return stripped === '' ? '/' : stripped;
};

// 루트 라우트 - Outlet으로 하위 라우트 렌더링
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  errorComponent: ErrorBoundary,
  notFoundComponent: NotFound, // 루트 레벨의 404 컴포넌트 설정
});

// 로그인 라우트
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.LOGIN,
  component: LoginPage,
});

// 회원가입 라우트
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.REGISTER,
  component: RegisterPage,
});

/** 임시 비밀번호 강제 변경 — API 플래그 연동 전 독립 진입 */
const forceChangePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.FORCE_CHANGE_PASSWORD,
  component: ForceChangePasswordPage,
});

// 어드민 레이아웃 라우트
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.ADMIN,
  component: AdminLayout,
  errorComponent: ErrorBoundary,
  notFoundComponent: NotFound, // 어드민 레벨의 404 컴포넌트 설정
});

// 시연 전용 레이아웃 라우트 — `/demo` URL에서만 시연 메뉴 노출
const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE_PATHS.DEMO,
  component: DemoLayout,
  errorComponent: ErrorBoundary,
  notFoundComponent: NotFound,
});

// 대시보드 라우트
const dashboardRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.DASHBOARD),
  component: DashboardPage,
});

const myAccountRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MY_ACCOUNT),
  component: MyAccountPage,
});

const settingsAccountsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTINGS_ACCOUNTS),
  component: SettingsAccountsPage,
});

const settingsAccountDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTINGS_ACCOUNT_DETAIL),
  component: SettingsAccountDetailPage,
});

const settingsAuditLogRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTINGS_AUDIT_LOG),
  component: SettingsAuditLogPage,
});

const settingsMenusRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTINGS_MENUS),
  component: SettingsMenusPage,
});

const settingsPartnersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTINGS_PARTNERS),
  component: SettingsPartnersPage,
});

// 사용자 관리 라우트
const usersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.USERS),
  component: UsersPage,
  validateSearch: passThroughSearch,
});

const userDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.USER_DETAIL),
  component: UserDetailPage,
});

// 재위탁 업체 관리 — `/new`를 `$companyId`보다 먼저 등록한다
const partnerCompanyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PARTNER_COMPANY),
  component: PartnerCompanyPage,
  validateSearch: passThroughSearch,
});

const partnerCompanyNewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PARTNER_COMPANY_NEW),
  component: PartnerCompanyCreatePage,
});

const partnerCompanyDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PARTNER_COMPANY_DETAIL),
  component: PartnerCompanyDetailPage,
});

// 병의원 라우트
const institutionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.INSTITUTIONS),
  component: InstitutionsPage,
  validateSearch: passThroughSearch,
});

const institutionDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.INSTITUTION_DETAIL),
  component: InstitutionDetailPage,
});

// 문전약국 라우트
const pharmaciesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PHARMACIES),
  component: PharmaciesPage,
  validateSearch: passThroughSearch,
});

const pharmacyDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PHARMACY_DETAIL),
  component: PharmacyDetailPage,
});

// 제약사 라우트
const pharmaCompaniesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PHARMA_COMPANIES),
  component: PharmaCompaniesPage,
  validateSearch: passThroughSearch,
});

const pharmaCompanyNewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PHARMA_COMPANY_NEW),
  component: PharmaCompanyCreatePage,
});

const pharmaCompanyDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PHARMA_COMPANY_DETAIL),
  component: PharmaCompanyDetailPage,
});

// 의약품 라우트
const drugsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.DRUGS),
  component: DrugsPage,
  validateSearch: passThroughSearch,
});

const drugDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.DRUG_DETAIL),
  component: DrugDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    refYm: typeof search.refYm === 'string' ? search.refYm : '',
  }),
});

const drugDataCreateRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.DRUG_DATA_CREATE),
  component: DrugDataCreatePage,
  validateSearch: (search: Record<string, unknown>) => ({
    refYm: typeof search.refYm === 'string' ? search.refYm : '',
  }),
});

const contractAppendixRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.CONTRACT_APPENDIX),
  component: ContractAppendixPage,
});

const baseFeeRatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.BASE_FEE_RATES),
  component: BaseFeeRatesPage,
  validateSearch: passThroughSearch,
});

const baseFeeRateSettingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.BASE_FEE_RATE_SETTING),
  component: BaseFeeRateSettingPage,
  validateSearch: (search: Record<string, unknown>) => ({
    settlementYearMonth:
      typeof search.settlementYearMonth === 'string'
        ? search.settlementYearMonth
        : '',
  }),
});

const detailFeeRatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.DETAIL_FEE_RATES),
  component: DetailFeeRatesPage,
  validateSearch: passThroughSearch,
});

const detailFeeRateSettingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.DETAIL_FEE_RATE_SETTING),
  component: DetailFeeRateSettingPage,
  validateSearch: (search: Record<string, unknown>) => ({
    userId: typeof search.userId === 'string' ? search.userId : '',
    clinicId: typeof search.clinicId === 'string' ? search.clinicId : '',
    basicFeeType:
      typeof search.basicFeeType === 'string' ? search.basicFeeType : 'RANGE',
  }),
});

const boardFaqRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.BOARD_FAQ),
  component: FaqPage,
});

const boardAnnouncementsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.BOARD_ANNOUNCEMENTS),
  component: AnnouncementsPage,
  validateSearch: passThroughSearch,
});

const boardAnnouncementNewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.BOARD_ANNOUNCEMENT_NEW),
  component: AnnouncementFormPage,
});

const boardAnnouncementEditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.BOARD_ANNOUNCEMENT_EDIT),
  component: AnnouncementFormPage,
  validateSearch: (search: Record<string, unknown>) => ({
    mode: typeof search.mode === 'string' ? search.mode : undefined,
  }),
});

// 약관 관리 — `/new`를 `$termsId`보다 먼저 등록해 접두 충돌을 피한다
const termsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.TERMS),
  component: TermsPage,
});

const termsNewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.TERMS_NEW),
  component: TermsFormPage,
  validateSearch: (search: Record<string, unknown>) => ({
    type: typeof search.type === 'string' ? search.type : undefined,
  }),
});

const termsDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.TERMS_DETAIL),
  component: TermsFormPage,
  validateSearch: (search: Record<string, unknown>) => ({
    mode: typeof search.mode === 'string' ? search.mode : undefined,
  }),
});

// 정산 수수료 라우트
const prescriptionReviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PRESCRIPTION_REVIEW),
  component: PrescriptionReviewPage,
  validateSearch: passThroughSearch,
});

const prescriptionReviewDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PRESCRIPTION_REVIEW_DETAIL),
  component: PrescriptionReviewDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    refYm: typeof search.refYm === 'string' ? search.refYm : '',
  }),
});

// 이전 처방 불러오기 — 상세 화면에서 새 창으로 연다 (AdminLayout이 사이드바 없이 그린다)
const prescriptionReviewPreviousDrugsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PRESCRIPTION_REVIEW_PREVIOUS_DRUGS),
  component: PreviousPrescriptionDrugsWindowPage,
});

const prescriptionHistoryRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PRESCRIPTION_HISTORY),
  component: PrescriptionHistoryPage,
  validateSearch: passThroughSearch,
});

const prescriptionHistoryDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PRESCRIPTION_HISTORY_DETAIL),
  component: PrescriptionHistoryDetailPage,
});

const prescriptionRegistrationDeadlineRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.PRESCRIPTION_REGISTRATION_DEADLINE),
  component: PrescriptionRegistrationDeadlinePage,
});

const clientsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.CLIENTS),
  component: ClientsPage,
  validateSearch: passThroughSearch,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.CLIENT_DETAIL),
  component: ClientDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    drugCode: typeof search.drugCode === 'string' ? search.drugCode : undefined,
    type: typeof search.type === 'string' ? search.type : undefined,
  }),
});

const settlementFeesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_FEES),
  component: SettlementFeesPage,
  validateSearch: passThroughSearch,
});

const settlementFeeDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_FEE_DETAIL),
  component: SettlementFeeDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    refYm: typeof search.refYm === 'string' ? search.refYm : '',
  }),
});

const settlementUploadRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_UPLOAD),
  component: SettlementUploadPage,
  validateSearch: passThroughSearch,
});

const settlementUploadDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_UPLOAD_DETAIL),
  component: SettlementUploadDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    settlementYm:
      typeof search.settlementYm === 'string' ? search.settlementYm : '',
    prescriptionYm:
      typeof search.prescriptionYm === 'string' ? search.prescriptionYm : '',
  }),
});

const prescriptionSettlementsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_PRESCRIPTIONS),
  component: PrescriptionSettlementsPage,
  validateSearch: passThroughSearch,
});

const prescriptionSettlementDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_PRESCRIPTION_DETAIL),
  component: PrescriptionSettlementDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    prescriptionYm:
      typeof search.prescriptionYm === 'string' ? search.prescriptionYm : '',
    settlementYm:
      typeof search.settlementYm === 'string' ? search.settlementYm : '',
    userId:
      typeof search.userId === 'number'
        ? search.userId
        : typeof search.userId === 'string' && search.userId.trim()
          ? Number(search.userId)
          : undefined,
  }),
});

const reverseTaxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.REVERSE_TAX),
  component: ReverseTaxPage,
});

const maxMatchRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH),
  component: MaxMatchInfoPage,
  validateSearch: passThroughSearch,
});

const maxMatchDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_DETAIL),
  component: MaxMatchInfoDetailPage,
  // 레거시: companyName/clinicId/page/size를 query로 유지 (새로고침 복원)
  validateSearch: (search: Record<string, unknown>) => {
    const asString = (value: unknown) => {
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
      return '';
    };
    const asNumber = (value: unknown, fallback: number) => {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) && n > 0 ? n : fallback;
    };
    return {
      companyName: asString(search.companyName),
      clinicId: asString(search.clinicId),
      page: asNumber(search.page, 1),
      pageSize: asNumber(search.pageSize, 25),
    };
  },
});

const maxMatchMiniRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_MINI),
  component: MaxMatchMiniPage,
  validateSearch: passThroughSearch,
});

const maxMatchMiniLikeDetailSearch = (search: Record<string, unknown>) => {
  const asString = (value: unknown) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    return '';
  };
  return {
    email: asString(search.email),
    companyName: asString(search.companyName),
    createdAt: asString(search.createdAt),
    maxMatchCreatedAt: asString(search.maxMatchCreatedAt),
    prescriptionYearMonth: asString(search.prescriptionYearMonth),
    status: asString(search.status),
  };
};

const maxMatchMiniDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_MINI_DETAIL),
  component: MaxMatchMiniDetailPage,
  // 레거시: 목록 행 클릭 시 상단 표시용 필드를 query로 전달
  validateSearch: maxMatchMiniLikeDetailSearch,
});

const maxMatchFullRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_FULL),
  component: MaxMatchFullPage,
  validateSearch: passThroughSearch,
});

const maxMatchFullDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_FULL_DETAIL),
  component: MaxMatchFullDetailPage,
});

const maxMatchOpsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_OPS),
  component: MaxMatchOpsPage,
  validateSearch: passThroughSearch,
});

const maxMatchOpsDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_OPS_DETAIL),
  component: MaxMatchOpsDetailPage,
});

const maxMatchOperatingHoursRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.MAX_MATCH_OPERATING_HOURS),
  component: MaxMatchOperatingHoursPage,
});

const settlementGeneratorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.SETTLEMENT_GENERATOR),
  component: SettlementInfoGeneratorPage,
});

const commissionAggregatorRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: removeAdminPrefix(ROUTE_PATHS.COMMISSION_AGGREGATOR),
  component: CommissionRateAggregatorPage,
});

const demoHomeRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: removeDemoPrefix(ROUTE_PATHS.DEMO_HOME),
  component: DemoHomePage,
});

const demoPostProcessRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: removeDemoPrefix(ROUTE_PATHS.DEMO_POST_PROCESS),
  component: DemoPostProcessPage,
});

const demoBatchJobsRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: removeDemoPrefix(ROUTE_PATHS.DEMO_BATCH_JOBS),
  component: DemoBatchJobsPage,
});

const demoBatchJobDetailRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: removeDemoPrefix(ROUTE_PATHS.DEMO_BATCH_JOB_DETAIL),
  component: DemoBatchJobDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    jobId: typeof search.jobId === 'string' ? search.jobId : '',
  }),
});

// 라우트 트리 구성
const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  forceChangePasswordRoute,
  adminRoute.addChildren([
    dashboardRoute,
    myAccountRoute,
    settingsAccountsRoute,
    settingsAccountDetailRoute,
    settingsAuditLogRoute,
    settingsMenusRoute,
    settingsPartnersRoute,
    usersRoute,
    userDetailRoute,
    partnerCompanyRoute,
    partnerCompanyNewRoute,
    partnerCompanyDetailRoute,
    institutionsRoute,
    institutionDetailRoute,
    pharmaciesRoute,
    pharmacyDetailRoute,
    pharmaCompaniesRoute,
    pharmaCompanyNewRoute,
    pharmaCompanyDetailRoute,
    drugsRoute,
    drugDetailRoute,
    drugDataCreateRoute,
    contractAppendixRoute,
    baseFeeRatesRoute,
    baseFeeRateSettingRoute,
    detailFeeRatesRoute,
    detailFeeRateSettingRoute,
    boardFaqRoute,
    boardAnnouncementsRoute,
    boardAnnouncementNewRoute,
    boardAnnouncementEditRoute,
    termsRoute,
    termsNewRoute,
    termsDetailRoute,
    settlementFeesRoute,
    settlementFeeDetailRoute,
    clientsRoute,
    clientDetailRoute,
    prescriptionReviewRoute,
    prescriptionReviewDetailRoute,
    prescriptionReviewPreviousDrugsRoute,
    prescriptionHistoryRoute,
    prescriptionHistoryDetailRoute,
    prescriptionRegistrationDeadlineRoute,
    settlementUploadRoute,
    settlementUploadDetailRoute,
    prescriptionSettlementsRoute,
    prescriptionSettlementDetailRoute,
    reverseTaxRoute,
    maxMatchMiniRoute,
    maxMatchMiniDetailRoute,
    maxMatchFullRoute,
    maxMatchFullDetailRoute,
    maxMatchOpsRoute,
    maxMatchOpsDetailRoute,
    maxMatchOperatingHoursRoute,
    maxMatchRoute,
    maxMatchDetailRoute,
    settlementGeneratorRoute,
    commissionAggregatorRoute,
  ]),
  demoRoute.addChildren([
    demoHomeRoute,
    demoPostProcessRoute,
    demoBatchJobsRoute,
    demoBatchJobDetailRoute,
  ]),
]);

// 라우터 생성
export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound, // 라우터 레벨의 기본 404 컴포넌트 설정
});

// 타입 선언
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
